import React from 'react';
import { Crown, Sparkles, Gift, UserMinus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/contexts/SubscriptionContext';

import { useTranslation } from 'react-i18next';

const ICONS = {
  trial: Gift,
  provision: Crown,
  plan_change: Sparkles,
  share_plan_removed: UserMinus,
};

export default function PlanNotificationModal() {
  const {
    t
  } = useTranslation();

  const { planNotification, dismissPlanNotification } = useSubscription();

  if (!planNotification) return null;

  const { type, tier_name, is_trial, duration_days, message } = planNotification;
  const Icon = ICONS[type] || Sparkles;
  const isTrial = is_trial || type === 'trial';
  const isShareRemoval = type === 'share_plan_removed';
  const accentColor = isShareRemoval ? '#ef4444' : (isTrial ? '#f59e0b' : '#10b981');

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="plan-notification-modal"
    >
      <div
        className="w-full max-w-sm rounded-2xl p-6 text-center space-y-5"
        style={{ background: 'var(--theme-surface, #1e293b)', border: '1px solid var(--glass-border, #334155)' }}
      >
        {/* Icon */}
        <div
          className="mx-auto w-16 h-16 rounded-full flex items-center justify-center"
          style={{ background: `${accentColor}22` }}
        >
          <Icon className="h-8 w-8" style={{ color: accentColor }} />
        </div>

        {/* Content */}
        <div>
          {isShareRemoval ? (
            <>
              <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--theme-text, #f1f5f9)' }}>
                {t('generated.plannotificationmodal_removed_from_share_3fc2')}
              </h3>
              <p className="text-sm" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
                {message || 'You have been removed from a Share Plan.'}
              </p>
              <p className="text-xs mt-2" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
                {t('generated.plannotificationmodal_your_account_has_2cdc')}
              </p>
            </>
          ) : (
            <>
              <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--theme-text, #f1f5f9)' }}>
                {isTrial ? 'You Got a Free Trial!' : 'Your Plan Has Changed'}
              </h3>

              {isTrial ? (
                <>
                  <p className="text-sm" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
                    {t('generated.plannotificationmodal_you_ve_been_0861')}{' '}
                    <strong style={{ color: accentColor }}>{duration_days}-day</strong> {t('generated.plannotificationmodal_trial_of_the_94b7')}{' '}
                    <strong style={{ color: 'var(--theme-text)' }}>{tier_name}</strong>plan.
                                      </p>
                  <p className="text-xs mt-2" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
                    {t('generated.plannotificationmodal_enjoy_full_access_6c21')} {tier_name} {t('generated.plannotificationmodal_features_your_trial_ecf7')}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
                    {t('generated.plannotificationmodal_you_are_now_cbff')}{' '}
                    <strong style={{ color: 'var(--theme-text)' }}>{tier_name}</strong>user.
                                      </p>
                  <p className="text-xs mt-2" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
                    {tier_name === 'Free'
                      ? 'Your plan has been changed to Free. Some features may be limited.'
                      : `You now have access to all ${tier_name} features.`}
                  </p>
                </>
              )}
            </>
          )}
        </div>

        {/* CTA */}
        <Button
          onClick={dismissPlanNotification}
          className="w-full rounded-xl py-5 font-medium text-base"
          style={{ background: isShareRemoval ? 'var(--theme-accent)' : accentColor, color: '#fff' }}
          data-testid="plan-notification-dismiss"
        >
          {isShareRemoval ? 'View Plans' : (isTrial ? 'Start Exploring' : 'Got It')}
        </Button>
      </div>
    </div>
  );
}
